<script setup lang="ts">
  import { TaskTypeTransformMap } from '@api/models/task';
  import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import { storeToRefs } from 'pinia';
  import { computed, inject, provide, ref, useTemplateRef, watch } from 'vue';
  import type { Ref } from 'vue';
  import { useRoute } from 'vue-router';

  import type { Task } from '@stuut/api/models';
  import { humanize } from '@stuut/common/src/utils';
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Button } from '@ui/components/button';
  import { Dialog, DialogContent } from '@ui/components/dialog';
  import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@ui/components/resizable';
  import { ScrollArea } from '@ui/components/scroll-area';
  import { Skeleton } from '@ui/components/skeleton';
  import { toast } from '@ui/components/sonner';
  import { Switch } from '@ui/components/switch';
  import { ToggleGroup, ToggleGroupItem } from '@ui/components/toggle-group';
  import { cn } from '@ui/utils/utils.ts';

  import { stuut } from '@/api';
  import TaskCompleteOverlay from '@/components/tasks/TaskCompleteOverlay.vue';
  import TaskDismissAlert from '@/components/tasks/TaskDismissAlert.vue';
  import TaskDrawerView from '@/components/tasks/TaskDrawerView.vue';
  import TaskHeader from '@/components/tasks/TaskHeader.vue';
  import { useTaskCollectionStore, useTaskStore } from '@/store';

  const route = useRoute();
  const showDismissAlert = ref(false);
  const showConfirmManualCall = ref(false);
  const TaskViewRef = ref();
  const saveManualCallNote = ref(true);
  const isExpanded = ref(false);
  const dismissAlertActionType = ref<string | undefined>();

  const closeDismissAlert = () => {
    showDismissAlert.value = false;
    dismissAlertActionType.value = undefined;
  };

  const openDismissAlert = (actionType?: string) => {
    showDismissAlert.value = true;
    dismissAlertActionType.value = actionType;
  };

  const dismissAlertData = computed(() => {
    if (currentTask.value?.isManualCall) {
      const notes = TaskViewRef.value?.ManualCallTaskRef?.notes;
      if (notes) return { manual_call_notes: notes };
    }
    return undefined;
  });

  const showEscapeCloseAlert = ref(false);
  const taskStore = useTaskStore();
  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);
  const {
    currentModalClassification,
    currentModalTaskType,
    currentTaskTypeCollections,
    isPartnerGroupView,
    subCollections,
    taskDisplayMode,
  } = storeToRefs(taskCollectionStore);
  const confirming = ref(false);
  const isDrawerOpen = inject('isDrawerOpen') as Ref<boolean>;
  const closeDrawer = inject<() => void>('closeDrawer');
  const forceTaskTableRefresh = inject<() => void>('forceTaskTableRefresh');
  const AfterCompleteOverlayRef = useTemplateRef('afterCompleteOverlayRef');

  const openConfirmManualCall = () => {
    showConfirmManualCall.value = true;
    saveManualCallNote.value = TaskViewRef.value?.ManualCallTaskRef?.isNotesDirty;
  };

  const isContactDropdownOpen = () => !!document.querySelector('[role="listbox"]');

  const handleCloseDrawer = (state: boolean) => {
    if (!state) {
      if (isContactDropdownOpen()) return;
      handleAlertClose();
    }
  };

  const handleAlertClose = (event?: any) => {
    event?.preventDefault();

    if (event?.currentTarget?.closest?.('.toaster')) return;

    if (
      currentTask?.value?.isOpen &&
      TaskViewRef.value?.TaskEmailResponseRef?.isDirty &&
      taskCollectionStore?.isTaskStackedMode
    ) {
      showEscapeCloseAlert.value = true;
    } else {
      closeDrawer?.();
    }
  };

  const editAwareClose = (event?: any) => {
    if (isContactDropdownOpen()) return;

    event?.preventDefault();

    if (AfterCompleteOverlayRef.value?.el) {
      return taskCollectionStore?.closeAfterCompleteOverlay();
    }

    if (TaskViewRef.value?.TaskEmailResponseRef?.editMode) {
      return TaskViewRef.value?.TaskEmailResponseRef?.editModeOff?.();
    }

    handleAlertClose(event);
  };

  const completeManualCall = async () => {
    if (!currentTask?.value) return;

    confirming.value = true;

    try {
      confirming.value = true;

      try {
        const response = await stuut.tasks.complete(currentTask?.value as Task, {
          close_reason: TaskViewRef.value?.ManualCallTaskRef?.notes,
        });

        toast.success('Task completed');

        if (saveManualCallNote.value && currentTask.value?.partner_id) {
          await stuut.notes.create({
            content: TaskViewRef.value?.ManualCallTaskRef?.notes,
            partner: currentTask.value?.partner_id,
          });
        }

        taskStore.updateAllTasks(currentTask.value);

        return response;
      } finally {
        forceTaskTableRefresh?.();
      }
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      showConfirmManualCall.value = false;
      confirming.value = false;
    }
  };

  const shouldCloseOnRouteChange = ref(false);

  const isCompactTaskType = computed(
    () =>
      currentTask?.value?.isEscalation ||
      currentTask?.value?.isManualCall ||
      currentTask?.value?.isPauseStateUpdate,
  );

  const enableCloseOnRouteChange = () => {
    shouldCloseOnRouteChange.value = true;
  };

  const showGroupWrapper = computed(
    () =>
      route.query?.group_view_mode === 'partner' || route.query?.group_view_mode === 'task_type',
  );

  const breakpoints = useBreakpoints(breakpointsTailwind);
  const isLgUp = breakpoints.greaterOrEqual('lg');
  const enableLeftSplitter = computed(() => isLgUp.value && showGroupWrapper.value);

  provide('editAwareClose', editAwareClose);

  watch(
    () => route.path,
    () => {
      if (isDrawerOpen.value && shouldCloseOnRouteChange.value) {
        closeDrawer?.();
        shouldCloseOnRouteChange.value = false;
      }
    },
  );
</script>

<template>
  <Dialog class="relative" :open="isDrawerOpen" @update:open="handleCloseDrawer">
    <DialogContent
      class="p-0 outline-none flex flex-col justify-between text-xs"
      :class="{
        'w-full lg:w-[1174px] lg:max-w-[1174px] xl:w-full xl:max-w-[1374px]': showGroupWrapper,
        'w-full lg:w-240 lg:max-w-240 xl:w-320 xl:max-w-320':
          !showGroupWrapper && !isCompactTaskType,
        'w-full lg:w-[762px] lg:max-w-[762px] xl:w-[762px] xl:max-w-[762px]':
          !showGroupWrapper && isCompactTaskType,
        'w-full lg:w-[662px] lg:max-w-[662px] xl:w-[962px] xl:max-w-[962px]':
          !showGroupWrapper && currentTask?.isDisputeActivate,
        'border-none': taskCollectionStore?.showAfterCompleteOverlay,
        'w-full! max-w-none! h-full': isExpanded,
      }"
      hide-close-btn
      :on-escape-key-down="editAwareClose"
      @pointer-down-outside="handleAlertClose"
    >
      <div :class="isExpanded ? 'w-full h-full' : 'h-[80vh] max-h-[800px]'">
        <div class="flex flex-1 w-full h-full">
          <div
            class="flex flex-1 flex-col overflow-hidden rounded z-10"
            style="transition: width 250ms cubic-bezier(0.4, 0, 0.2, 1)"
          >
            <div class="bg-background top-0 sticky z-1">
              <TaskHeader
                v-if="currentTask?.id"
                :task="currentTask"
                :is-expanded="isExpanded"
                :partner="
                  TaskViewRef?.TaskEmailResponseRef?.partner ||
                  TaskViewRef?.DisableContactTaskRef?.partner ||
                  TaskViewRef?.ManualCallTaskRef?.partner
                "
                @toggle-fullscreen="isExpanded = !isExpanded"
              />
            </div>

            <component
              :is="enableLeftSplitter ? ResizablePanelGroup : 'div'"
              v-bind="enableLeftSplitter ? { direction: 'horizontal' } : {}"
              class="flex h-full overflow-hidden flex-1 rounded overflow-x-hidden"
            >
              <component
                :is="enableLeftSplitter ? ResizablePanel : 'div'"
                v-if="showGroupWrapper"
                v-bind="
                  enableLeftSplitter
                    ? { id: 'task-drawer-left', order: 1, defaultSize: 20, minSize: 5, maxSize: 25 }
                    : {}
                "
                :class="enableLeftSplitter ? undefined : 'contents'"
              >
                <ScrollArea
                  class="@container [&>div]:outline-transparent relative"
                  :class="enableLeftSplitter ? 'h-full w-full' : 'lg:h-full overflow-x-visible!'"
                  :style="enableLeftSplitter ? undefined : { width: 'var(--left-width)' }"
                >
                  <div
                    class="relative pt-5"
                    :class="enableLeftSplitter ? 'w-full min-w-[200px]' : 'shrink-0'"
                    :style="enableLeftSplitter ? undefined : { width: 'var(--left-width)' }"
                  >
                    <ToggleGroup
                      class="px-5 w-full"
                      :model-value="taskDisplayMode"
                      default-value="stacked"
                      type="single"
                      variant="primary"
                      @update:model-value="
                        (value) => {
                          if (value === 'list') {
                            return taskCollectionStore?.showTaskList();
                          }

                          return taskCollectionStore?.showTaskStack();
                        }
                      "
                    >
                      <ToggleGroupItem value="stacked" class="flex-1">
                        <i class="fa far fa-list-dropdown text-2xs!"></i>
                        <span>Detail</span>
                      </ToggleGroupItem>

                      <ToggleGroupItem value="list" class="flex-1">
                        <i class="fa far fa-list text-2xs!"></i>
                        <span>List</span>
                      </ToggleGroupItem>
                    </ToggleGroup>

                    <div
                      v-if="
                        taskCollectionStore.isLoadingTask && !taskCollectionStore.isInitNavLoaded
                      "
                      class="flex flex-col gap-4 flex-1 py-5 px-5 w-full"
                    >
                      <div class="flex items-center">
                        <div class="flex-1 space-y-2.5 gap-y-4">
                          <Skeleton class="h-5 flex-1 rounded" />
                          <div class="flex-1 space-y-2.5 ml-4 pl-4 border-l-2">
                            <Skeleton class="h-5 flex-1 rounded" />
                            <Skeleton class="h-5 flex-1 rounded" />
                            <Skeleton class="h-5 flex-1 rounded" />
                          </div>
                          <Skeleton class="h-5 flex-1 rounded" />
                          <Skeleton class="h-5 flex-1 rounded" />
                        </div>
                      </div>
                    </div>

                    <div v-else class="space-y-1 py-5 px-5 mr-px">
                      <RouterLink
                        v-for="collection in currentTaskTypeCollections"
                        :key="collection?.id"
                        class="block"
                        :to="{
                          query: {
                            ...route?.query,
                            collection_id: collection?.id,
                            collection_type: collection?.collection_type,
                            sub_collection_id: undefined,
                            sub_collection_type: undefined,
                            task_drawer_id: undefined,
                          },
                        }"
                        @click="taskCollectionStore.navigationDirection = undefined"
                      >
                        <Button
                          variant="ghost"
                          :class="
                            cn(
                              'flex items-center w-full justify-between text-sm h-11 hover:bg-secondary px-2 disabled:opacity-100',
                              collection?.id === currentModalTaskType &&
                                'text-secondary-foreground font-bold',
                            )
                          "
                          :disabled="collection?.id === currentModalTaskType"
                          size="sm"
                        >
                          <span class="truncate text-xs">
                            {{
                              TaskTypeTransformMap[collection?.id] ||
                              humanize(collection?.name || '')
                            }}
                          </span>

                          <div
                            :class="
                              cn(
                                'flex shrink-0 items-center justify-center rounded size-6 text-center text-xs',
                                collection?.id === currentModalTaskType &&
                                  'bg-secondary text-secondary-foreground font-semibold',
                              )
                            "
                          >
                            {{ collection?.task_count }}
                          </div>
                        </Button>

                        <div
                          v-if="
                            collection?.id === currentModalTaskType &&
                            collection?.showSubCollections
                          "
                          class="ml-4 mr-0 pl-2 space-y-0.5 border-l-2"
                        >
                          <div
                            v-if="
                              taskCollectionStore.isLoadingTask &&
                              !taskCollectionStore.isInitSubNavLoaded
                            "
                            class="flex flex-col gap-4 flex-1 py-5 px-5 w-full"
                          >
                            <div class="flex items-center">
                              <div class="flex-1 space-y-2.5 gap-y-4">
                                <Skeleton class="h-5 flex-1 rounded" />
                                <div class="flex-1 space-y-2.5 ml-4 pl-4 border-l-2">
                                  <Skeleton class="h-5 flex-1 rounded" />
                                  <Skeleton class="h-5 flex-1 rounded" />
                                  <Skeleton class="h-5 flex-1 rounded" />
                                </div>
                                <Skeleton class="h-5 flex-1 rounded" />
                                <Skeleton class="h-5 flex-1 rounded" />
                              </div>
                            </div>
                          </div>
                          <RouterLink
                            v-for="subCollection in subCollections"
                            :key="subCollection?.id"
                            class="block"
                            replace
                            :to="{
                              query: {
                                ...route?.query,
                                task_drawer_id: undefined,
                                ...(isPartnerGroupView
                                  ? {
                                      sub_collection_id: subCollection?.id,
                                      sub_collection_type: subCollection?.collection_type,
                                    }
                                  : {
                                      sub_collection_id: subCollection?.id,
                                      sub_collection_type: subCollection?.collection_type,
                                    }),
                              },
                            }"
                            @click="taskCollectionStore.navigationDirection = undefined"
                          >
                            <Button
                              :class="
                                cn(
                                  'w-full justify-between items-center text-sm h-10 hover:bg-secondary truncate px-2 rounded disabled:opacity-100',
                                  subCollection?.id === currentModalClassification &&
                                    'bg-secondary font-bold text-secondary-foreground',
                                )
                              "
                              variant="ghost"
                              :disabled="subCollection?.id === currentModalClassification"
                              size="sm"
                            >
                              <span class="truncate text-xs">
                                {{
                                  TaskTypeTransformMap[subCollection?.id] ||
                                  humanize(subCollection?.name || '')
                                }}
                              </span>

                              <div
                                :class="
                                  cn(
                                    'flex shrink-0 items-center justify-center rounded size-6 text-center text-xs',
                                    subCollection?.id === currentModalClassification &&
                                      'bg-transparent font-bold text-foreground',
                                  )
                                "
                              >
                                {{ subCollection?.task_count }}
                              </div>
                            </Button>
                          </RouterLink>
                        </div>
                      </RouterLink>
                    </div>
                  </div>
                  <div
                    class="bg-background flex flex-nowrap h-12 w-full items-center justify-between px-5 py-2 border-t bottom-0 absolute"
                  ></div>
                </ScrollArea>
              </component>

              <ResizableHandle
                v-if="enableLeftSplitter"
                with-handle
                class="items-start pt-[300px]"
              />

              <component
                :is="enableLeftSplitter ? ResizablePanel : 'div'"
                v-bind="
                  enableLeftSplitter ? { id: 'task-drawer-right', order: 2, defaultSize: 80 } : {}
                "
                :class="enableLeftSplitter ? 'flex' : 'contents'"
              >
                <TaskDrawerView
                  ref="TaskViewRef"
                  :key="currentTask?.id"
                  @enable-close-on-route-change="enableCloseOnRouteChange"
                  @open-dismiss-alert="openDismissAlert"
                  @open-confirm-complete="openConfirmManualCall"
                  @close="closeDrawer"
                />
              </component>
            </component>
          </div>

          <TaskCompleteOverlay ref="afterCompleteOverlayRef" :task="currentTask" />
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <AlertDialog
    :open="showConfirmManualCall"
    @update:open="(status) => (showConfirmManualCall = status)"
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle> Complete Task </AlertDialogTitle>

        <AlertDialogDescription class="space-y-4">
          <p>Do you want to mark this task as complete?</p>

          <div class="space-y-2 px-3.5 pb-3.5 border rounded max-h-[150px] overflow-y-auto">
            <p class="font-bold text-muted-foreground sticky top-0 pt-3.5 pb-0.5 bg-secondary">
              Notes:
            </p>

            <div class="text-2xs whitespace-pre-line">
              {{ TaskViewRef?.ManualCallTaskRef?.notes }}
            </div>
          </div>

          <div class="flex items-center gap-x-2 justify-start">
            <span class="text-2xs">Save note to customer page</span>

            <Switch
              name="saveManualCallNote"
              :model-value="saveManualCallNote"
              @update:model-value="(event) => (saveManualCallNote = event)"
            />
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel :disabled="confirming" @click="showConfirmManualCall = false">
          Cancel
        </AlertDialogCancel>

        <AlertDialogAction :disabled="confirming" :loading="confirming" @click="completeManualCall">
          Complete
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <AlertDialog
    :open="showEscapeCloseAlert"
    @update:open="(status) => (showEscapeCloseAlert = status)"
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
        <AlertDialogDescription>
          If you exit you will lose any unsaved changes.
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel @click="showEscapeCloseAlert = false">Cancel </AlertDialogCancel>
        <AlertDialogAction @click="closeDrawer?.()">Leave</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <TaskDismissAlert
    v-if="currentTask"
    :task="currentTask"
    :show="showDismissAlert"
    :action-type="dismissAlertActionType"
    :data="dismissAlertData"
    @close="closeDismissAlert"
  />
</template>

<style scoped></style>
